<script setup lang="ts">
import type { ChartData, ChartOptions } from "chart.js";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import type { DeepReadonly } from "vue";
import { computed } from "vue";
import { Bar as BarChart, Pie as PieChart } from "vue-chartjs";

import type { SightlineChartBlock } from "@/backend/natsstubs/sightline/models";
import color from "@/model/color.module.scss";

import SightlineResultHeader from "./SightlineResultHeader.vue";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const props = defineProps<{
  block: DeepReadonly<SightlineChartBlock>;
  fullView?: boolean;
  triggerId?: string;
}>();
const emit = defineEmits<{ open: [] }>();

// The wire carries labels + values only; colors are assigned by index so the
// same answer always looks the same.
const palette = [
  color.stickerBlue,
  color.stickerGreen,
  color.stickerYellow,
  color.stickerRed,
  color.stickerPurple,
  color.stickerOrange,
];

const paletteColor = (index: number) => palette[index % palette.length];

const pieData = computed<ChartData<"pie">>(() => ({
  labels: [...props.block.categories],
  datasets: props.block.series.map((series) => ({
    label: series.name,
    data: [...series.values],
    backgroundColor: props.block.categories.map((_, index) => paletteColor(index)),
  })),
}));

const barData = computed<ChartData<"bar">>(() => ({
  labels: [...props.block.categories],
  datasets: props.block.series.map((series, index) => ({
    label: series.name,
    data: [...series.values],
    backgroundColor: paletteColor(index),
    borderRadius: 4,
    maxBarThickness: 28,
  })),
}));

const pieOptions = computed<ChartOptions<"pie">>(() => ({
  animation: false,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom" },
  },
}));

const barOptions = computed<ChartOptions<"bar">>(() => ({
  animation: false,
  maintainAspectRatio: false,
  indexAxis: "y",
  scales: {
    x: { border: { display: false } },
    y: { border: { display: false }, grid: { display: false } },
  },
  plugins: {
    legend: { display: props.block.series.length > 1 },
  },
}));

const height = computed(() => {
  if (props.block.kind === "pie") return props.fullView ? 400 : 240;
  const rowHeight = props.fullView ? 36 : 28;
  return Math.min(props.fullView ? 640 : 360, 64 + props.block.categories.length * rowHeight);
});

const label = computed(() => {
  const values = props.block.series[0]?.values;
  if (!values) return props.block.categories.join(", ");
  const facts = props.block.categories.map((category, index) => `${category}: ${values[index]}`);
  return facts.join(", ");
});
</script>

<template>
  <div class="sightline-chart-block">
    <SightlineResultHeader
      v-if="!fullView"
      :title="$t('sightline.chart')"
      :trigger-id="triggerId"
      @open="emit('open')"
    />
    <div class="chart-box">
      <figure class="chart-canvas" :style="{ height: `${height}px` }">
        <PieChart
          v-if="block.kind === 'pie'"
          :data="pieData"
          :options="pieOptions"
          role="img"
          :aria-label="label"
        />
        <BarChart v-else :data="barData" :options="barOptions" role="img" :aria-label="label" />
      </figure>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";

.sightline-chart-block {
  width: 100%;

  &:hover,
  &:focus-within {
    --sightline-expand-opacity: 1;
  }

  .chart-box {
    padding: 16px;
    border: 1px solid colors.$border-light;
    border-radius: 8px;
    background: colors.$background-ui;
  }

  .chart-canvas {
    position: relative;
    min-width: 0;
    margin: 0;
  }
}
</style>
